import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
// 直接从firebase/firestore导入，防止重导出问题
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  setDoc,
  addDoc
} from 'firebase/firestore';

// 引入数据提取工具函数
import { 
  safeGet, 
  extractCustomerName, 
  extractCustomerEmail, 
  extractCustomerPhone,
  extractPaymentAmount,
  extractPaymentStatus,
  extractPaymentId,
  extractShippingAddress,
  extractItems,
  formatPrice,
  formatDate,
  formatTimeSlots,
  getStatusColor
} from '../../utils/dataExtractors';

// 从我们自己的文件只导入db实例
import { db } from '../../firebase/firebase';

const OrderManagement = () => {
  // 注意：已经从 dataExtractors.js 导入了 safeGet 函数，不需要在这里重新定义
  
  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [error, setError] = useState(null);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [showRawData, setShowRawData] = useState(false);

  // Order status options
  const statusOptions = [
    { value: 'pending', label: '待付款', color: 'warning' },
    { value: 'paid', label: '已付款', color: 'success' },
    { value: 'processing', label: '处理中', color: 'default' },
    { value: 'shipped', label: '已发货', color: 'primary' },
    { value: 'delivered', label: '已送达', color: 'warning' },
    { value: 'completed', label: '已付款', color: 'success' },
    { value: 'cancelled', label: '已取消', color: 'error' },
    { value: 'refunded', label: '已退款', color: 'error' },
  ];

  // Load orders data
  const loadOrders = async (reset = false) => {
    try {
      setLoading(reset);
      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      // 1. 构建查询
      console.log('开始加载订单数据');
      let ordersQuery = collection(db, 'orders');
      let constraints = [];

      if (statusFilter !== 'all') {
        constraints.push(where('status', '==', statusFilter));
      }

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(10));

      if (lastVisible && !reset) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(ordersQuery, ...constraints);
      
      // 2. 执行查询
      console.log('正在执行Firestore查询');
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('查询结果为空');
        setHasMore(false);
        setLoadingMore(false);
        setLoading(false);
        return;
      }

      console.log(`获取到${querySnapshot.docs.length}个订单`);
      const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(newLastVisible);

      // 3. 处理每个订单 - 使用单独的函数处理，而不是 map，以避免 Promise.all 问题
      const newOrders = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        try {
          const orderData = docSnapshot.data();
          const orderId = docSnapshot.id;
          
          // 增加调试日志，查看完整订单信息
          console.log(`处理订单 ${orderId}，字段:`, Object.keys(orderData).join(', '));
          
          // 使用工具函数提取关键信息 - 记录每一步以便调试
          let customerName, customerEmail, customerPhone, totalAmount, paymentStatus, paymentId, shippingAddress, orderItems;
          
          try {
            customerName = extractCustomerName(orderData);
            console.log(`提取客户名称: ${customerName}`);
          } catch (err) {
            console.error('提取客户名称失败:', err);
            customerName = '未知客户';
          }
          
          try {
            customerEmail = extractCustomerEmail(orderData);
            console.log(`提取客户邮箱: ${customerEmail}`);
          } catch (err) {
            console.error('提取客户邮箱失败:', err);
            customerEmail = '';
          }
          
          try {
            customerPhone = extractCustomerPhone(orderData);
            console.log(`提取客户电话: ${customerPhone}`);
          } catch (err) {
            console.error('提取客户电话失败:', err);
            customerPhone = '';
          }
          
          try {
            totalAmount = extractPaymentAmount(orderData);
            console.log(`提取订单金额: ${totalAmount}`);
          } catch (err) {
            console.error('提取订单金额失败:', err);
            totalAmount = 0;
          }
          
          try {
            paymentStatus = extractPaymentStatus(orderData);
            console.log(`提取支付状态: ${paymentStatus}`);
          } catch (err) {
            console.error('提取支付状态失败:', err);
            paymentStatus = 'pending';
          }
          
          try {
            paymentId = extractPaymentId(orderData);
            console.log(`提取支付ID: ${paymentId}`);
          } catch (err) {
            console.error('提取支付ID失败:', err);
            paymentId = '';
          }
          
          try {
            shippingAddress = extractShippingAddress(orderData);
            console.log(`提取配送地址: ${JSON.stringify(shippingAddress)}`);
          } catch (err) {
            console.error('提取配送地址失败:', err);
            shippingAddress = {};
          }
          
          try {
            orderItems = extractItems(orderData);
            console.log(`提取订单商品: ${orderItems.length}个商品`);
          } catch (err) {
            console.error('提取订单商品失败:', err);
            orderItems = [];
          }
          
          // 处理运费信息
          let shippingFee = 0;
          try {
            if (orderData.shipping && typeof orderData.shipping === 'number') {
              shippingFee = orderData.shipping;
            } else if (orderData.amount && orderData.amount.shipping) {
              shippingFee = parseFloat(orderData.amount.shipping) || 0;
            } else if (orderData.purchase_units && 
                      orderData.purchase_units[0] && 
                      orderData.purchase_units[0].amount && 
                      orderData.purchase_units[0].amount.breakdown && 
                      orderData.purchase_units[0].amount.breakdown.shipping && 
                      orderData.purchase_units[0].amount.breakdown.shipping.value) {
              shippingFee = parseFloat(orderData.purchase_units[0].amount.breakdown.shipping.value) || 0;
            }
            console.log(`处理运费信息: ${shippingFee}`);
          } catch (err) {
            console.error('处理运费信息失败:', err);
            shippingFee = 0;
          }
          
          // Get user data if available and fill in missing information
          let userData = null;
          
          // 首先使用订单中已有的userInfo
          if (orderData.userInfo) {
            userData = orderData.userInfo;
            console.log(`使用订单中的userInfo: ${JSON.stringify(userData)}`);
          } 
          // 如果没有userInfo但有userId，尝试从users集合获取
          else if (orderData.userId) {
            try {
              console.log(`从用户集合获取订单${orderId}的用户数据, userId=${orderData.userId}`);
              const userDocRef = doc(db, 'users', orderData.userId);
              
              const userDocSnapshot = await getDoc(userDocRef);
              
              if (userDocSnapshot.exists()) {
                userData = userDocSnapshot.data();
                console.log(`获取到用户数据: ${JSON.stringify(userData)}`);
                
                // 修复：如果customerName、customerEmail、customerPhone未能从orderData中提取，
                // 则直接从userData获取
                if (!customerName || customerName === '未知客户') {
                  customerName = userData.displayName || '未知客户';
                  console.log(`从用户数据更新客户名称: ${customerName}`);
                }
                
                if (!customerEmail || customerEmail === '') {
                  customerEmail = userData.email || '';
                  console.log(`从用户数据更新客户邮箱: ${customerEmail}`);
                }
                
                if (!customerPhone || customerPhone === '') {
                  customerPhone = userData.phoneNumber || userData.phone || '';
                  console.log(`从用户数据更新客户电话: ${customerPhone}`);
                }
              } else {
                console.log(`用户ID ${orderData.userId} 不存在`);
              }
            } catch (error) {
              console.error(`获取用户数据失败:`, error);
              userData = null;
            }
          }

          // 处理支付日志，获取最新状态和处理记录
          let processingStatus = null;
          let lastProcessed = null;
          
          try {  
            if (orderData.processingLog && Array.isArray(orderData.processingLog) && orderData.processingLog.length > 0) {
              // 获取最新的处理记录
              const latestLog = orderData.processingLog[orderData.processingLog.length - 1];
              processingStatus = latestLog.details && latestLog.details.status;
              lastProcessed = latestLog.details && latestLog.details.timestamp;
            }
            
            // 获取支付保护状态
            let sellerProtection = null;
            if (orderData.purchase_units && 
                orderData.purchase_units[0] && 
                orderData.purchase_units[0].payments && 
                orderData.purchase_units[0].payments.captures && 
                orderData.purchase_units[0].payments.captures[0] && 
                orderData.purchase_units[0].payments.captures[0].seller_protection) {
              sellerProtection = orderData.purchase_units[0].payments.captures[0].seller_protection.status;
            }
            
            // 获取货币代码
            let currencyCode = 'SGD'; // 默认新加坡币
            if (orderData.currency) {
              currencyCode = orderData.currency;
            } else if (orderData.captureAmount && orderData.captureAmount.currencyCode) {
              currencyCode = orderData.captureAmount.currencyCode;
            } else if (orderData.purchase_units && 
                       orderData.purchase_units[0] && 
                       orderData.purchase_units[0].payments && 
                       orderData.purchase_units[0].payments.captures && 
                       orderData.purchase_units[0].payments.captures[0] && 
                       orderData.purchase_units[0].payments.captures[0].amount && 
                       orderData.purchase_units[0].payments.captures[0].amount.currency_code) {
              currencyCode = orderData.purchase_units[0].payments.captures[0].amount.currency_code;
            }
            
            // 获取国家/地区代码
            let countryCode = 'SG'; // 默认新加坡
            if (orderData.shipping && orderData.shipping.address && orderData.shipping.address.country_code) {
              countryCode = orderData.shipping.address.country_code;
            } else if (orderData.purchase_units && 
                      orderData.purchase_units[0] && 
                      orderData.purchase_units[0].shipping && 
                      orderData.purchase_units[0].shipping.address && 
                      orderData.purchase_units[0].shipping.address.country_code) {
              countryCode = orderData.purchase_units[0].shipping.address.country_code;
            } else if (shippingAddress && shippingAddress.country) {
              countryCode = shippingAddress.country;
            }
          
            // 确保顾客电话信息正确
          if (userData && userData.phoneNumber && (!customerPhone || customerPhone === '')) {
            customerPhone = userData.phoneNumber;
            console.log(`从用户数据直接设置客户电话: ${customerPhone}`);
          }
            
          // 保留原始订单的金额数据，不进行任何计算
          // 直接使用Firestore orders集合中的原始数据
          
          // 获取订单原始的shipping费用(运费)
          let shippingFee = 0;
          // 首先尝试从amount.shipping获取运费
          if (safeGet(orderData, 'amount.shipping') !== undefined) {
            shippingFee = typeof orderData.amount.shipping === 'number' ? 
                         orderData.amount.shipping : 
                         parseFloat(orderData.amount.shipping) || 0;
            console.log(`使用订单amount.shipping数据: ${shippingFee}`);
          } 
          // 如果没有amount.shipping，则尝试使用shipping
          else if (orderData.shipping !== undefined) {
            shippingFee = typeof orderData.shipping === 'number' ? 
                         orderData.shipping : 
                         parseFloat(orderData.shipping) || 0;
            console.log(`使用订单原始shipping数据: ${shippingFee}`);
          }
          
          // 获取订单原始的subtotal(商品小计)
          let subtotal = 0;
          // 首先尝试从amount.subtotal获取商品小计
          if (safeGet(orderData, 'amount.subtotal') !== undefined) {
            subtotal = typeof orderData.amount.subtotal === 'number' ? 
                      orderData.amount.subtotal : 
                      parseFloat(orderData.amount.subtotal) || 0;
            console.log(`使用订单amount.subtotal数据: ${subtotal}`);
          }
          // 如果没有amount.subtotal，则尝试使用subtotal
          else if (orderData.subtotal !== undefined) {
            subtotal = typeof orderData.subtotal === 'number' ? 
                      orderData.subtotal : 
                      parseFloat(orderData.subtotal) || 0;
            console.log(`使用订单原始subtotal数据: ${subtotal}`);
          }
          
          // 获取订单原始的total(订单总计)
          let totalAmount = 0;
          // 首先尝试从amount.total获取订单总计
          if (safeGet(orderData, 'amount.total') !== undefined) {
            totalAmount = typeof orderData.amount.total === 'number' ? 
                         orderData.amount.total : 
                         parseFloat(orderData.amount.total) || 0;
            console.log(`使用订单amount.total数据: ${totalAmount}`);
          }
          // 如果没有amount.total，则尝试使用total
          else if (orderData.total !== undefined) {
            totalAmount = typeof orderData.total === 'number' ? 
                         orderData.total : 
                         parseFloat(orderData.total) || 0;
            console.log(`使用订单原始total数据: ${totalAmount}`);
          }
            
            // 创建处理完的订单对象，合并所有数据
            // 首先保留原始订单数据，然后添加额外处理的信息
            const processedOrder = {
              id: orderId,
              ...orderData,  // 保留原始订单数据的所有字段(包括amount.subtotal, amount.shipping, amount.total)
              userData,
              customerName,
              customerEmail,
              customerPhone,
              paymentId: paymentId || "",
              currencyCode,
              countryCode,
              processingStatus: processingStatus || paymentStatus,
              lastProcessed,
              sellerProtection,
              status: orderData.status || processingStatus || paymentStatus,
              payer: orderData.payer || {},
              orderNumber: orderId,
              shippingAddress,
              items: orderItems.length > 0 ? orderItems : (orderData.items || []),
              totalAmount: safeGet(orderData, 'amount.total') || totalAmount, // 确保价格数据可以访问
              shipping: safeGet(orderData, 'amount.shipping') || shippingFee, // 确保运费数据可以访问
              subtotal: safeGet(orderData, 'amount.subtotal') || subtotal     // 确保小计数据可以访问
            };
            
            // 添加到结果数组
            newOrders.push(processedOrder);
            
          } catch (error) {
            console.error(`处理订单 ${docSnapshot.id} 时出错:`, error);
          }
        } catch (error) {
          console.error(`处理订单文档时出错:`, error);
        }
      }
          
      // 设置状态
      if (reset) {
        setOrders(newOrders);
      } else {
        setOrders(prev => [...prev, ...newOrders]);
      }
    
      setHasMore(querySnapshot.docs.length === 10);
    } catch (error) {
      console.error('加载订单失败:', error);
      setError('加载订单失败');
      setSnackbar({
        open: true,
        message: '加载订单失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and when filter changes
  useEffect(() => {
    loadOrders(true);
  }, [statusFilter]);

  // Handle search
  const handleSearch = () => {
    if (searchTerm.trim() !== '') {
      const filtered = orders.filter(
        order => 
          order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (order.customerName && order.customerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (order.customerEmail && order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (order.customerPhone && order.customerPhone.includes(searchTerm)) ||
          (order.shippingAddress && order.shippingAddress.phone && 
           order.shippingAddress.phone.includes(searchTerm)) ||
          (order.userData && order.userData.phoneNumber && 
           order.userData.phoneNumber.includes(searchTerm))
      );
      setOrders(filtered);
      setHasMore(false);
    } else {
      loadOrders(true);
    }
  };

  // View order details
  const handleViewOrder = async (id) => {
    console.log(`准备查看订单详情: ${id}`);
    try {
      setLoading(true);
      
      // 防止ID为空或无效
      if (!id) {
        console.error('无效的订单ID');
        setSnackbar({
          open: true,
          message: '无效的订单ID',
          severity: 'error',
        });
        return;
      }
      
      // 创建文档引用
      const orderDocRef = doc(db, 'orders', id);
      
      try {
        // 获取订单数据
        const orderDocSnapshot = await getDoc(orderDocRef);
        
        // 检查文档是否存在
        if (orderDocSnapshot.exists()) {
          const orderData = orderDocSnapshot.data();
          
          // 使用工具函数提取额外的数据
          const customerName = extractCustomerName(orderData);
          const customerEmail = extractCustomerEmail(orderData);
          const customerPhone = extractCustomerPhone(orderData);
          const totalAmount = extractPaymentAmount(orderData);
          const paymentStatus = extractPaymentStatus(orderData);
          const paymentId = extractPaymentId(orderData);
          const shippingAddress = extractShippingAddress(orderData);
          const orderItems = extractItems(orderData);
          
          // 处理运费信息
          let shippingFee = 0;
          // 首先尝试从amount.shipping获取运费
          if (safeGet(orderData, 'amount.shipping') !== undefined) {
            shippingFee = parseFloat(safeGet(orderData, 'amount.shipping')) || 0;
          } 
          // 然后尝试从shipping获取
          else if (safeGet(orderData, 'shipping') && typeof orderData.shipping === 'number') {
            shippingFee = orderData.shipping;
          } 
          // 最后尝试从PayPal数据获取
          else if (safeGet(orderData, 'purchase_units.0.amount.breakdown.shipping.value')) {
            shippingFee = parseFloat(safeGet(orderData, 'purchase_units.0.amount.breakdown.shipping.value')) || 0;
          }
          
          // 处理商品小计
          let subtotal = 0;
          // 首先尝试从amount.subtotal获取商品小计
          if (safeGet(orderData, 'amount.subtotal') !== undefined) {
            subtotal = parseFloat(safeGet(orderData, 'amount.subtotal')) || 0;
          }
          // 然后尝试从subtotal获取
          else if (safeGet(orderData, 'subtotal') !== undefined) {
            subtotal = parseFloat(safeGet(orderData, 'subtotal')) || 0;
          }
          // 最后使用totalAmount减去shippingFee
          else {
            subtotal = totalAmount - shippingFee;
          }
          
          // 处理订单总价
          let total = 0;
          // 首先尝试从amount.total获取订单总计
          if (safeGet(orderData, 'amount.total') !== undefined) {
            total = parseFloat(safeGet(orderData, 'amount.total')) || 0;
          }
          // 然后尝试从total获取
          else if (safeGet(orderData, 'total') !== undefined) {
            total = parseFloat(safeGet(orderData, 'total')) || 0;
          }
          // 最后使用totalAmount
          else {
            total = totalAmount;
          }
          
          // 增强订单数据
          const enhancedOrderData = {
            ...orderData,
            id,
            orderNumber: id,
            customerName,
            customerEmail,
            customerPhone,
            totalAmount: total || totalAmount,
            paymentStatus,
            paymentId,
            shippingAddress,
            items: orderItems.length > 0 ? orderItems : orderData.items || [],
            shipping: shippingFee,
            subtotal: subtotal,
            total: total || totalAmount,
            currencyCode: safeGet(orderData, 'currency') || 
                       safeGet(orderData, 'captureAmount.currencyCode') || 
                       safeGet(orderData, 'purchase_units.0.payments.captures.0.amount.currency_code') || 
                       'SGD',
          };
          
          // 设置状态并显示对话框
          setSelectedOrderDetails(enhancedOrderData);
          setSelectedOrder(id);
          setOpenDialog(true);
          setDetailsTabValue(0); // 重置为第一个标签页
          setShowRawData(false); // 默认不显示原始数据
        } else {
          console.error(`订单 ${id} 不存在`);
          setSnackbar({
            open: true,
            message: '找不到订单数据',
            severity: 'error',
          });
        }
      } catch (docError) {
        console.error('获取订单文档失败:', docError);
        setError('获取订单文档失败');
        setSnackbar({
          open: true,
          message: '获取订单文档失败: ' + docError.message,
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('加载订单数据失败:', error);
      setError('加载订单数据失败');
      setSnackbar({
        open: true,
        message: '加载订单数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Start status update process
  const handleStatusUpdateClick = (id, currentStatus) => {
    setSelectedOrder(id);
    setNewStatus(currentStatus);
    setStatusUpdateDialog(true);
  };

  // Update order status and send email notification
  const handleStatusUpdate = async () => {
    // 在函数顶部定义变量，避免作用域问题
    let notificationSent = false;
    let updatedOrderId = selectedOrder;
    let updatedStatus = newStatus;
    
    try {
      setLoading(true);
      console.log('开始更新订单状态:', { orderId: selectedOrder, newStatus });
      
      // 1. 获取订单数据
      const orderDocRef = doc(db, 'orders', selectedOrder);
      let orderData = null;
      
      try {
        const orderDocSnapshot = await getDoc(orderDocRef);
        
        if (!orderDocSnapshot.exists()) {
          throw new Error('订单不存在');
        }
        
        orderData = orderDocSnapshot.data();
        console.log('获取到订单数据:', { orderId: selectedOrder, orderFields: Object.keys(orderData) });
      } catch (fetchError) {
        console.error('获取订单数据失败:', fetchError);
        throw new Error(`获取订单数据失败: ${fetchError.message}`);
      }
      
      // 2. 更新订单状态
      try {
        console.log('准备更新订单状态', { orderId: selectedOrder, status: newStatus });
        
        await updateDoc(orderDocRef, {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
        
        console.log('订单状态更新成功');
      } catch (updateError) {
        console.error('更新订单状态失败:', updateError);
        throw new Error(`更新订单状态失败: ${updateError.message}`);
      }
      
      // 3. 发送邮件通知
      try {
        // 安全地获取邮箱地址 - 使用safeGet确保安全访问
        const customerEmail = safeGet(orderData, 'customerEmail') || 
                              safeGet(orderData, 'userInfo.email') || 
                              safeGet(orderData, 'userData.email') ||
                              safeGet(orderData, 'userEmail');
                              
        const paypalEmail = safeGet(orderData, 'paypalEmail') || 
                           safeGet(orderData, 'payer.email_address') ||
                           safeGet(orderData, 'payment.payer.email_address');
        
        if (customerEmail || paypalEmail) {
          // 准备收件人列表
          const toEmails = [];
          if (customerEmail) toEmails.push(customerEmail);
          if (paypalEmail && paypalEmail !== customerEmail) toEmails.push(paypalEmail);
          
          // 获取状态中文名称
          let statusName = "";
          if (newStatus === 'shipped') {
            statusName = "已发货";
          } else if (newStatus === 'delivered') {
            statusName = "已送达";
          } else if (newStatus === 'refunded') {
            statusName = "已退款";
          } else {
            // 获取状态中文名称
            const statusOption = statusOptions.find(option => option.value === newStatus);
            statusName = statusOption ? statusOption.label : newStatus;
          }
          
          // 创建邮件通知文档
          const mailDoc = {
            to: toEmails,
            message: {
              subject: `订单状态更新: ${selectedOrder}`,
              text: `您的订单 ${selectedOrder} 状态已更新为: ${statusName}。\n\n感谢您的支持！`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2>订单状态更新</h2>
                  <p>尊敬的客户，您好！</p>
                  <p>您的订单 <strong>${selectedOrder}</strong> 状态已更新为: <strong>${statusName}</strong></p>
                  <hr style="border: 1px solid #eee; margin: 20px 0;">
                  <p>如有任何问题，请随时联系我们。</p>
                  <p>感谢您的支持！</p>
                </div>
              `
            },
            createdAt: new Date()
          };
          
          // 保存到 mail 集合，使用 Cloud Functions 触发器发送邮件
          await setDoc(doc(collection(db, 'mail')), mailDoc);
          console.log('邮件通知创建成功:', toEmails);
          notificationSent = toEmails.length > 0;
        }
      } catch (mailError) {
        console.error('发送邮件通知失败:', mailError);
        // 继续处理，邮件失败不影响订单状态更新
      }

      // 4. 更新本地状态
      setOrders(orders.map(item => {
        if (item.id === selectedOrder) {
          return { ...item, status: newStatus };
        }
        return item;
      }));

      // 5. 显示成功提示
      setSnackbar({
        open: true,
        message: '订单状态已更新' + (notificationSent ? '，并已发送邮件通知' : ''),
        severity: 'success',
      });
      
      setStatusUpdateDialog(false);
    } catch (error) {
      console.error('订单处理失败:', error);
      setError('更新订单状态失败');
      setSnackbar({
        open: true,
        message: '更新订单失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Confirm delete before proceeding
  const handleDeleteConfirm = (id) => {
    setSelectedOrder(id);
    setOpenDeleteDialog(true);
  };

  // Handle delete order
  const handleDelete = async () => {
    try {
      setLoading(true);
      const orderDocRef = doc(db, 'orders', selectedOrder);
      
      try {
        // Get the order details first to check for booking information
        const orderDoc = await getDoc(orderDocRef);
        if (!orderDoc.exists()) {
          throw new Error('订单不存在');
        }
        
        const orderData = orderDoc.data();
        console.log(`删除订单前获取详情:`, orderData);
        
        // Check if this order is associated with a booking
        const bookingId = orderData.bookingId;
        let bookingData = null;
        
        if (bookingId) {
          console.log(`订单关联了预约ID: ${bookingId}，获取预约详情`);
          try {
            const bookingRef = doc(db, 'bookings', bookingId);
            const bookingDoc = await getDoc(bookingRef);
            
            if (bookingDoc.exists()) {
              bookingData = bookingDoc.data();
              console.log(`获取到预约详情:`, bookingData);
              
              // Delete the associated booking as well
              await deleteDoc(bookingRef);
              console.log(`已删除关联的预约 ${bookingId}`);
              
              // Add a log entry for the deleted booking
              try {
                const logEntry = {
                  action: 'BOOKING_DELETED_WITH_ORDER',
                  orderId: selectedOrder,
                  bookingId: bookingId,
                  fortuneTellerId: bookingData.fortuneTellerId,
                  timeSlots: bookingData.timeSlots || [bookingData.timeSlot],
                  formattedDate: bookingData.formattedDate,
                  adminUid: 'admin', // Replace with actual admin ID if available
                  timestamp: serverTimestamp()
                };
                
                await addDoc(collection(db, 'activityLogs'), logEntry);
                console.log('已记录预约删除活动');
              } catch (logError) {
                console.error('记录预约删除活动失败:', logError);
              }
            }
          } catch (bookingError) {
            console.error(`获取或删除预约 ${bookingId} 失败:`, bookingError);
            // Continue with the order deletion even if booking operations fail
          }
        }
        
        // Delete the order
        await deleteDoc(orderDocRef);
        console.log(`已删除订单 ${selectedOrder}`);
        
        // Update local state
        setOrders(orders.filter(item => item.id !== selectedOrder));
        
        // Prepare success message
        let successMessage = '订单已删除';
        if (bookingId) {
          successMessage += '，相关预约也已删除';
        }
        
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success',
        });
        
        setOpenDeleteDialog(false);
      } catch (deleteError) {
        console.error('删除订单文档失败:', deleteError);
        setSnackbar({
          open: true,
          message: '删除订单失败: ' + deleteError.message,
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('处理删除操作失败:', error);
      setError('删除订单失败');
      setSnackbar({
        open: true,
        message: '删除订单失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setSelectedOrder(null);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  
  // Handle tab change in details dialog
  const handleTabChange = (event, newValue) => {
    setDetailsTabValue(newValue);
  };

  // Get status chip based on payment status and order status
  const getStatusChip = (status) => {
    // 先处理空或未定义状态
    if (!status) {
      return <Chip label="未知状态" color="default" size="small" />;
    }
    
    // 确保状态是字符串
    const statusStr = String(status);
    
    // 查找已定义的状态选项
    const statusOption = statusOptions.find(option => option.value === statusStr.toLowerCase());
    if (statusOption) {
      return <Chip label={statusOption.label} color={statusOption.color} size="small" />;
    }
    
    // 处理特殊状态字符串
    if (statusStr === 'COMPLETED' || statusStr.toLowerCase() === 'completed') {
      return <Chip label="已付款" color="success" size="small" />;
    } else if (statusStr === 'CREATED' || statusStr.toLowerCase() === 'created') {
      return <Chip label="已创建" color="default" size="small" />;
    } else if (statusStr.toLowerCase() === 'paid') {
      return <Chip label="已付款" color="success" size="small" />;
    } else if (statusStr.toLowerCase() === 'shipped') {
      return <Chip label="已发货" color="primary" size="small" />;
    } else if (statusStr.toLowerCase() === 'delivered') {
      return <Chip label="已送达" color="warning" size="small" />;
    } else if (statusStr.toLowerCase() === 'refunded') {
      return <Chip label="已退款" color="error" size="small" />;
    } else if (statusStr.toLowerCase() === 'cancelled') {
      return <Chip label="已取消" color="error" size="small" />;
    } else if (statusStr.toLowerCase() === 'pending') {
      return <Chip label="待付款" color="warning" size="small" />;
    } else if (statusStr.toLowerCase() === 'processing') {
      return <Chip label="处理中" color="default" size="small" />;
    }
    
    // 使用工具函数获取状态颜色
    const color = getStatusColor(statusStr);
    
    // 根据状态特征确定显示文本
    const lowercaseStatus = statusStr.toLowerCase();
    if (lowercaseStatus.includes('paid') || 
        lowercaseStatus.includes('complete') || 
        lowercaseStatus.includes('approved')) {
      return <Chip label="已付款" color="success" size="small" />;
    } else if (lowercaseStatus.includes('ship')) {
      return <Chip label="已发货" color="primary" size="small" />;
    } else if (lowercaseStatus.includes('deliver')) {
      return <Chip label="已送达" color="warning" size="small" />;
    } else if (lowercaseStatus.includes('refund')) {
      return <Chip label="申请退款" color="error" size="small" />;
    } else if (lowercaseStatus.includes('cancel')) {
      return <Chip label="已取消" color="error" size="small" />;
    } else if (lowercaseStatus.includes('creat') || 
               lowercaseStatus.includes('pend') || 
               lowercaseStatus.includes('process')) {
      return <Chip label="处理中" color="default" size="small" />;
    }
    
    // 对于完全未知的状态，显示原始文本
    return <Chip label={statusStr} color="default" size="small" />;
  };

  // 这些函数已从 dataExtractors.js 导入，无需重复定义
  // 如果需要可以在这里添加自定义逻辑

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ my: 4 }}>
      {/* Toolbar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="搜索订单"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>状态</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="状态"
            >
              <MenuItem value="all">全部</MenuItem>
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadOrders(true)}
            color="primary"
            variant="outlined"
            size="medium"
          >
            刷新
          </Button>
        </Box>
      </Box>

      {/* Orders table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="订单列表">
          <TableHead>
            <TableRow>
              <TableCell>订单ID</TableCell>
              <TableCell>日期</TableCell>
              <TableCell>客户</TableCell>
              <TableCell>联系电话</TableCell>
              <TableCell align="right">金额</TableCell>
              <TableCell align="center">状态</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  暂无数据
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Typography variant="body2">{order.id}</Typography>
                  </TableCell>
                  <TableCell>
                    {formatDate(order.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{order.customerName}</Typography>
                    <Typography variant="caption" color="textSecondary">{order.customerEmail}</Typography>
                  </TableCell>
                  <TableCell>
                    {order.customerPhone}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatPrice(safeGet(order, 'amount.total') || order.totalAmount || order.total || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getStatusChip(order.status)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="查看详情">
                        <IconButton
                          color="info"
                          onClick={() => handleViewOrder(order.id)}
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已发货按钮 */}
                      <Tooltip title="设为已发货">
                        <IconButton
                          color={order.status === 'shipped' ? "success" : "default"}
                          onClick={() => handleStatusUpdateClick(order.id, 'shipped')}
                          size="small"
                        >
                          <ShippingIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已送达按钮 */}
                      <Tooltip title="设为已送达">
                        <IconButton
                          color={order.status === 'delivered' ? "success" : "default"}
                          onClick={() => handleStatusUpdateClick(order.id, 'delivered')}
                          size="small"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已退款按钮 */}
                      <Tooltip title="设为已退款">
                        <IconButton
                          color={order.status === 'refunded' ? "error" : "default"}
                          onClick={() => handleStatusUpdateClick(order.id, 'refunded')}
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 删除订单按钮 */}
                      <Tooltip title="删除订单">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteConfirm(order.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load more button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            onClick={() => loadOrders(false)}
            disabled={loadingMore}
            variant="outlined"
            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
          >
            {loadingMore ? '加载中...' : '加载更多'}
          </Button>
        </Box>
      )}

      {/* Order details dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">订单详情 - ID: {selectedOrder}</Typography>
            {selectedOrderDetails && getStatusChip(selectedOrderDetails.status)}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrderDetails && (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={detailsTabValue} onChange={handleTabChange} aria-label="订单详情标签">
                  <Tab label="基本信息" id="tab-0" />
                  <Tab label="客户信息" id="tab-1" />
                  <Tab label="商品明细" id="tab-2" />
                  <Tab label="支付详情" id="tab-3" />
                </Tabs>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked={showRawData} onChange={(e) => setShowRawData(e.target.checked)} />}
                  label="显示原始数据"
                />
              </Box>
              
              {/* 基本信息 Tab */}
              {detailsTabValue === 0 && (
                <Grid container spacing={3}>
                  {/* Order Summary */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">
                            订单概览
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>订单号:</strong> {selectedOrder}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>下单时间:</strong> {formatDate(selectedOrderDetails.createdAt)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>订单状态:</strong> {
                                selectedOrderDetails.status === 'pending' ? '待付款' :
                                selectedOrderDetails.status === 'paid' ? '已付款' :
                                selectedOrderDetails.status === 'processing' ? '处理中' :
                                selectedOrderDetails.status === 'shipped' ? '已发货' :
                                selectedOrderDetails.status === 'delivered' ? '已送达' :
                                selectedOrderDetails.status === 'completed' ? '已完成' :
                                selectedOrderDetails.status === 'cancelled' ? '已取消' :
                                selectedOrderDetails.status === 'refunded' ? '已退款' :
                                selectedOrderDetails.status || 'N/A'
                              }
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>付款状态:</strong> {
                                selectedOrderDetails.paymentStatus === 'pending' ? '待付款' :
                                selectedOrderDetails.paymentStatus === 'completed' || 
                                selectedOrderDetails.paymentStatus === 'COMPLETED' ? '已付款' :
                                selectedOrderDetails.paymentStatus === 'refunded' ? '已退款' :
                                selectedOrderDetails.paymentStatus || 'N/A'
                              }
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>支付方式:</strong> {
                                safeGet(selectedOrderDetails, 'payment.method') || 
                                (selectedOrderDetails.paymentId ? 'PayPal' : 'N/A')
                              }
                            </Typography>
                            {selectedOrderDetails.updatedAt && (
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>最后更新:</strong> {formatDate(selectedOrderDetails.updatedAt)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        
                        <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" fontWeight="bold">
                                商品小计: {formatPrice(safeGet(selectedOrderDetails, 'amount.subtotal') || selectedOrderDetails.subtotal || 0)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" fontWeight="bold">
                                运费: {formatPrice(safeGet(selectedOrderDetails, 'amount.shipping') || selectedOrderDetails.shipping || 0)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" fontWeight="bold">
                                订单总计: {formatPrice(safeGet(selectedOrderDetails, 'amount.total') || selectedOrderDetails.total || 0)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Shipping Info - 仅显示在基本信息页 */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          配送信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {selectedOrderDetails.shippingAddress ? (
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body1" gutterBottom>
                                <strong>收件人:</strong> {selectedOrderDetails.shippingAddress.name || selectedOrderDetails.shippingAddress.fullName || selectedOrderDetails.customerName || '未提供'}
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                <strong>电话:</strong> {selectedOrderDetails.shippingAddress.phone || selectedOrderDetails.customerPhone || '未提供'}
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                <strong>国家/地区:</strong> {selectedOrderDetails.shippingAddress.country || '未提供'}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body1" gutterBottom>
                                <strong>地址:</strong> {
                                  selectedOrderDetails.shippingAddress.addressLine || 
                                  selectedOrderDetails.shippingAddress.address || 
                                  [
                                    selectedOrderDetails.shippingAddress.state,
                                    selectedOrderDetails.shippingAddress.city,
                                    selectedOrderDetails.shippingAddress.district,
                                    selectedOrderDetails.shippingAddress.address
                                  ].filter(Boolean).join(', ') || 
                                  '未提供'
                                }
                              </Typography>
                              {(selectedOrderDetails.shippingAddress.postalCode || selectedOrderDetails.shippingAddress.zipCode) && (
                                <Typography variant="body1" gutterBottom>
                                  <strong>邮编:</strong> {selectedOrderDetails.shippingAddress.postalCode || selectedOrderDetails.shippingAddress.zipCode}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        ) : (
                          <Typography variant="body1">无配送信息</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Order Notes - 仅显示在基本信息页 */}
                  {selectedOrderDetails.notes && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            订单备注
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="body1">
                            {selectedOrderDetails.notes}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}
              
              {/* 客户信息 Tab */}
              {detailsTabValue === 1 && (
                <Grid container spacing={3}>
                  {/* Customer Info */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          客户信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body1" gutterBottom>
                              <strong>姓名:</strong> {selectedOrderDetails.customerName || (selectedOrderDetails.userData && selectedOrderDetails.userData.displayName) || '未提供姓名'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>电子邮件:</strong> <span style={{ color: '#1976d2' }}>
                                {selectedOrderDetails.customerEmail || (selectedOrderDetails.userData && selectedOrderDetails.userData.email) || '未提供邮箱'}
                              </span>
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>电话:</strong> {
                                selectedOrderDetails.customerPhone || 
                                (selectedOrderDetails.userData && selectedOrderDetails.userData.phoneNumber) || 
                                (selectedOrderDetails.userData && selectedOrderDetails.userData.phone) || 
                                '未提供电话'
                              }
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            {safeGet(selectedOrderDetails, 'payer.payer_id') && (
                              <Typography variant="body1" gutterBottom>
                                <strong>PayPal ID:</strong> {safeGet(selectedOrderDetails, 'payer.payer_id')}
                              </Typography>
                            )}
                            {safeGet(selectedOrderDetails, 'payment_source.paypal.account_status') && (
                              <Typography variant="body1" gutterBottom>
                                <strong>账户状态:</strong> {safeGet(selectedOrderDetails, 'payment_source.paypal.account_status')}
                              </Typography>
                            )}
                            {selectedOrderDetails.userId && (
                              <Typography variant="body1" gutterBottom>
                                <strong>用户ID:</strong> {selectedOrderDetails.userId}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        
                        {/* 额外的客户数据展示 */}
                        {selectedOrderDetails.userData && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              用户档案信息
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                              {Object.entries(selectedOrderDetails.userData).map(([key, value]) => {
                                // 过滤掉敏感或冗余信息
                                if (key === 'password' || key === 'salt' || key === '_id' || key === '__v') return null;
                                
                                const displayValue = typeof value === 'object' && value !== null
                                  ? JSON.stringify(value)
                                  : String(value);
                                  
                                return (
                                  <Grid item xs={12} sm={6} key={key}>
                                    <Typography variant="body2" gutterBottom>
                                      <strong>{key}:</strong> {displayValue}
                                    </Typography>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 商品明细 Tab */}
              {detailsTabValue === 2 && (
                <Grid container spacing={3}>
                  {/* Order Items */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          订单商品
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {selectedOrderDetails.items && selectedOrderDetails.items.length > 0 ? (
                          <>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>商品名称</TableCell>
                                    <TableCell align="center">数量</TableCell>
                                    <TableCell align="right">单价</TableCell>
                                    <TableCell align="right">小计</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedOrderDetails.items.map((item, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{item.name || '未知商品'}</TableCell>
                                      <TableCell align="center">{item.quantity || 1}</TableCell>
                                      <TableCell align="right">{formatPrice(item.price || 0)}</TableCell>
                                      <TableCell align="right">
                                        {formatPrice((item.price || 0) * (item.quantity || 1))}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}></Grid>
                              <Grid item xs={12} sm={6}>
                                <List>
                                  <ListItem disablePadding sx={{ py: 1 }}>
                                    <ListItemText primary="商品小计" />
                                    <Typography variant="body1">
                                      {formatPrice(safeGet(selectedOrderDetails, 'amount.subtotal') || selectedOrderDetails.subtotal || 0)}
                                    </Typography>
                                  </ListItem>
                                  
                                  <ListItem disablePadding sx={{ py: 1 }}>
                                    <ListItemText primary="运费" />
                                    <Typography variant="body1">
                                      {formatPrice(safeGet(selectedOrderDetails, 'amount.shipping') || selectedOrderDetails.shipping || 0)}
                                    </Typography>
                                  </ListItem>
                                  
                                  {selectedOrderDetails.discount != null && (
                                    <ListItem disablePadding sx={{ py: 1 }}>
                                      <ListItemText primary="折扣" />
                                      <Typography variant="body1" color="error">
                                        -{formatPrice(selectedOrderDetails.discount)}
                                      </Typography>
                                    </ListItem>
                                  )}
                                  
                                  <Divider sx={{ my: 1 }} />
                                  
                                  <ListItem disablePadding sx={{ py: 1 }}>
                                    <ListItemText primary={<Typography variant="h6">总计</Typography>} />
                                    <Typography variant="h6" color="primary">
                                      {formatPrice(safeGet(selectedOrderDetails, 'amount.total') || selectedOrderDetails.total || 0)}
                                    </Typography>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>
                          </>
                        ) : (
                          <Typography variant="body1" align="center" sx={{ py: 2 }}>
                            无商品明细
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 支付详情 Tab */}
              {detailsTabValue === 3 && (
                <Grid container spacing={3}>
                  {/* Payment Info */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          支付信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={4}>
                            <Typography variant="body1" gutterBottom>
                              <strong>支付方式:</strong> {safeGet(selectedOrderDetails, 'payment.method') || 'PayPal'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={4}>
                            <Typography variant="body1" gutterBottom>
                              <strong>支付状态:</strong> {
                                selectedOrderDetails.paymentStatus === 'completed' ? '已支付' :
                                selectedOrderDetails.paymentStatus === 'pending' ? '待付款' :
                                selectedOrderDetails.paymentStatus === 'refunded' ? '已退款' :
                                selectedOrderDetails.paymentStatus || '未知'
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={4}>
                            <Typography variant="body1" gutterBottom>
                              <strong>付款货币:</strong> {selectedOrderDetails.currencyCode || 'SGD'}
                            </Typography>
                          </Grid>
                          
                          {selectedOrderDetails.paymentId && (
                            <Grid item xs={12}>
                              <Typography variant="body1" gutterBottom>
                                <strong>支付ID:</strong> {selectedOrderDetails.paymentId}
                              </Typography>
                            </Grid>
                          )}
                          
                          {safeGet(selectedOrderDetails, 'payer.email_address') && (
                            <Grid item xs={6} md={4}>
                              <Typography variant="body1" gutterBottom>
                                <strong>PayPal邮箱:</strong> {safeGet(selectedOrderDetails, 'payer.email_address')}
                              </Typography>
                            </Grid>
                          )}
                          
                          {/* 支付时间信息 */}
                          {safeGet(selectedOrderDetails, 'createdAt') && (
                            <Grid item xs={6} md={6}>
                              <Typography variant="body1" gutterBottom>
                                <strong>支付创建时间:</strong> {formatDate(safeGet(selectedOrderDetails, 'createdAt'))}
                              </Typography>
                            </Grid>
                          )}
                          
                          {safeGet(selectedOrderDetails, 'updatedAt') && (
                            <Grid item xs={6} md={6}>
                              <Typography variant="body1" gutterBottom>
                                <strong>支付更新时间:</strong> {formatDate(safeGet(selectedOrderDetails, 'updatedAt'))}
                              </Typography>
                            </Grid>
                          )}
                          
                          {/* 添加支付保护状态 */}
                          {safeGet(selectedOrderDetails, 'purchase_units.0.payments.captures.0.seller_protection.status') && (
                            <Grid item xs={12}>
                              <Typography variant="body1" gutterBottom>
                                <strong>卖家保护状态:</strong> {safeGet(selectedOrderDetails, 'purchase_units.0.payments.captures.0.seller_protection.status')}
                              </Typography>
                            </Grid>
                          )}
                          
                          {/* 净收款金额 */}
                          {safeGet(selectedOrderDetails, 'purchase_units.0.payments.captures.0.seller_receivable_breakdown.net_amount.value') && (
                            <Grid item xs={6} md={4}>
                              <Typography variant="body1" gutterBottom>
                                <strong>净收款金额:</strong> {formatPrice(safeGet(selectedOrderDetails, 'purchase_units.0.payments.captures.0.seller_receivable_breakdown.net_amount.value'))}
                              </Typography>
                            </Grid>
                          )}
                          
                          {/* PayPal手续费 */}
                          {safeGet(selectedOrderDetails, 'purchase_units.0.payments.captures.0.seller_receivable_breakdown.paypal_fee.value') && (
                            <Grid item xs={6} md={4}>
                              <Typography variant="body1" gutterBottom>
                                <strong>PayPal手续费:</strong> {formatPrice(safeGet(selectedOrderDetails, 'purchase_units.0.payments.captures.0.seller_receivable_breakdown.paypal_fee.value'))}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                        
                        {/* PayPal支付源信息 */}
                        {safeGet(selectedOrderDetails, 'payer') && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              PayPal付款人信息
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            
                            <Grid container spacing={2}>
                              {safeGet(selectedOrderDetails, 'payer.email_address') && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>电子邮箱:</strong> {safeGet(selectedOrderDetails, 'payer.email_address')}
                                  </Typography>
                                </Grid>
                              )}
                              
                              {safeGet(selectedOrderDetails, 'payer.name') && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>姓名:</strong> {
                                      `${safeGet(selectedOrderDetails, 'payer.name.given_name') || ''} ${safeGet(selectedOrderDetails, 'payer.name.surname') || ''}`.trim() || 'N/A'
                                    }
                                  </Typography>
                                </Grid>
                              )}
                              
                              {safeGet(selectedOrderDetails, 'payer.payer_id') && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>PayPal ID:</strong> {safeGet(selectedOrderDetails, 'payer.payer_id')}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                        
                        {/* 处理日志信息 */}
                        {safeGet(selectedOrderDetails, 'processingLog') && Array.isArray(selectedOrderDetails.processingLog) && selectedOrderDetails.processingLog.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              处理日志
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Box sx={{ ml: 2 }}>
                              {selectedOrderDetails.processingLog.map((log, index) => (
                                <Box key={index} sx={{ mb: 1 }}>
                                  <Typography variant="body2">
                                    <strong>步骤:</strong> {safeGet(log, 'details.step')}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>时间:</strong> {safeGet(log, 'details.timestamp')}
                                  </Typography>
                                  {safeGet(log, 'details.status') && (
                                    <Typography variant="body2">
                                      <strong>状态:</strong> {safeGet(log, 'details.status')}
                                    </Typography>
                                  )}
                                  {safeGet(log, 'details.amount') && (
                                    <Typography variant="body2">
                                      <strong>金额:</strong> {safeGet(log, 'details.amount')}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 原始数据显示 */}
              {showRawData && (
                <Box sx={{ mt: 3 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        原始数据（调试用）
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <pre style={{ 
                        background: '#f5f5f5', 
                        padding: '8px', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '400px'
                      }}>
                        {JSON.stringify(selectedOrderDetails, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>关闭</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setOpenDialog(false);
              handleStatusUpdateClick(selectedOrder, selectedOrderDetails.status);
            }}
          >
            更新状态
          </Button>
          <Button
            color="error"
            variant="outlined"
            onClick={() => {
              setOpenDialog(false);
              handleDeleteConfirm(selectedOrder);
            }}
          >
            删除订单
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status update dialog */}
      <Dialog
        open={statusUpdateDialog}
        onClose={() => setStatusUpdateDialog(false)}
      >
        <DialogTitle>更新订单状态</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            请选择新的订单状态:
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel>状态</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="状态"
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialog(false)}>取消</Button>
          <Button
            onClick={handleStatusUpdate}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除这个订单吗？此操作不可撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>取消</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderManagement;